import { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import Link from './ui/Link';
import useCompanyProfile from '../stores/useCompanyProfiles';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LanguageContext } from '../contexts/LanguageContext';
import useAssetStore from '../stores/useAssetStore';

export default function LandingNav() {
  const { assets, loadAssets } = useAssetStore();
  const { profile } = useCompanyProfile();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { language, changeLanguage } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const { t } = useTranslation();
  const lang = language === 'id';

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setIsSidebarOpen(false);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleNavClick = () => {
    setIsSidebarOpen(false);
  };

  const phoneNumber = profile?.whatsapp_pr;
  const message = lang
    ? `Halo, saya tertarik dengan layanan Anda. Mohon informasi lebih lanjut mengenai pilihan layanan, harga, dan ketersediaan. Terima kasih!`
    : `Hello, I am interested in your services. Could you please provide more details about the available options, pricing, and availability? Thank you!`;
  const whatsappUrl = `https://wa.me/+62${phoneNumber}?text=${encodeURIComponent(message)}`;

  return (
    <nav
      className={`fixed top-0 left-0 w-full shadow-md z-50 transition-all duration-300 ${
        !isSidebarOpen && isScrolled ? 'bg-primary/80 backdrop-blur-md border-b border-b-secondary' : 'bg-primary'
      }`}
    >
      <div className="flex justify-center">
        <div className="flex w-full justify-between items-center mx-auto px-4 gap-4 max-w-screen-xl py-2">
          <div>
            <Link to="/">
              <img src={assets.logo} className="w-12 md:w-20 rounded" alt="Logo Company" />
            </Link>
          </div>
          <div className="flex gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  className="p-2 hover:bg-neutral-50/10 text-sm bg-inherit rounded-lg flex gap-1 uppercase"
                >
                  <img src={language === 'id' ? '/assets/id.svg' : '/assets/gb.svg'} alt="inggris" className="w-5" />
                  {language}
                  <ChevronDown className="h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-border bg-white shadow border p-1 rounded-lg">
                <DropdownMenuItem>
                  <Button
                    variant="link"
                    className="w-full justify-start"
                    type="button"
                    onClick={() => changeLanguage('id')}
                  >
                    <img src="/assets/id.svg" alt="inggris" className="w-5" />
                    Bahasa Indonesia
                  </Button>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Button
                    variant="link"
                    className="w-full justify-start"
                    type="button"
                    onClick={() => changeLanguage('en')}
                  >
                    <img src="/assets/gb.svg" alt="inggris" className="w-5" />
                    English
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {isMobile ? (
              <>
                <button type="button" onClick={handleToggleSidebar} className="p-2 text-white rounded-lg flex gap-1">
                  <Menu className="h-6 w-6" />
                </button>

                <div
                  className={`fixed top-0 right-0 h-full w-64 bg-primary/95 shadow-lg transition-all duration-300 ${
                    isSidebarOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
                  } z-50`}
                  style={{ willChange: 'transform, opacity' }}
                >
                  <div className="flex justify-between items-center p-4 border-b border-b-secondary">
                    <h2 className="text-white font-semibold">Menu</h2>
                    <button type="button" onClick={handleToggleSidebar} className="p-2 text-white rounded-lg">
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  <ul className="flex flex-col text-sm font-semibold text-white p-4 gap-4">
                    <li className="hover:text-secondary transition-colors duration-300">
                      <Link to="/" onClick={handleNavClick}>
                        {t('navigation.home')}
                      </Link>
                    </li>
                    <li className="hover:text-secondary transition-colors duration-300">
                      <Link to="/services" onClick={handleNavClick}>
                        {t('navigation.services')}
                      </Link>
                    </li>
                    <li className="hover:text-secondary transition-colors duration-300">
                      <Link to="/deals" onClick={handleNavClick}>
                        {t('navigation.deals')}
                      </Link>
                    </li>
                    <li className="hover:text-secondary transition-colors duration-300">
                      <Link to="/contact-us" onClick={handleNavClick}>
                        {t('navigation.contact')}
                      </Link>
                    </li>
                    <li className="hover:text-secondary transition-colors duration-300">
                      <Link to="/about-us" onClick={handleNavClick}>
                        {t('navigation.about')}
                      </Link>
                    </li>
                    <li className="hover:text-secondary transition-colors duration-300">
                      <Link to={whatsappUrl} target="_blank" onClick={handleNavClick}>
                        <Button variant="secondaryBackground" className="w-full flex items-center gap-2">
                          <img src="/assets/Whatsapp.svg" alt="whatsapp logo" />
                          {t('navigation.booking')}
                        </Button>
                      </Link>
                    </li>
                  </ul>
                </div>

                {isSidebarOpen && (
                  <button
                    type="button"
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300"
                    onClick={handleToggleSidebar}
                    aria-label="Close Sidebar"
                  />
                )}
              </>
            ) : (
              <ul className="flex md:text-base items-center text-sm gap-4 font-semibold text-white">
                <li className="hover:text-secondary transition-colors duration-300">
                  <Link to="/">{t('navigation.home')}</Link>
                </li>
                <li className="hover:text-secondary transition-colors duration-300">
                  <Link to="/services">{t('navigation.services')}</Link>
                </li>
                <li className="hover:text-secondary transition-colors duration-300">
                  <Link to="/deals">{t('navigation.deals')}</Link>
                </li>
                <li className="hover:text-secondary transition-colors duration-300">
                  <Link to="/contact-us">{t('navigation.contact')}</Link>
                </li>
                <li className="hover:text-secondary transition-colors duration-300">
                  <Link to="/about-us">{t('navigation.about')}</Link>
                </li>
                <li className="hover:text-secondary transition-colors duration-300">
                  <Link to={whatsappUrl} target="_blank">
                    <Button variant="secondaryBackground">
                      <img src="/assets/Whatsapp.svg" alt="whatsapp logo" />
                      {t('navigation.booking')}
                    </Button>
                  </Link>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
